// Kleine Bausteine aus dem Design-System: Knopf, Status, Kennzahl, Zustand, Banner, Feld, Segment, Chip.
import { LoaderCircle, type LucideIcon } from "lucide-react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from "react";
import { useId } from "react";
import { Link } from "react-router";

type KnopfArt = "standard" | "haupt" | "leise" | "gefahr";

export function Knopf({ art = "standard", klein, symbol: Icon, laedt, children, className, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { art?: KnopfArt; klein?: boolean; symbol?: LucideIcon; laedt?: boolean }) {
  const klassen = ["im-btn",
    art === "haupt" && "im-btn--primary", art === "leise" && "im-btn--ghost", art === "gefahr" && "im-btn--danger",
    klein && "im-btn--sm", className].filter(Boolean).join(" ");
  return (
    <button type="button" className={klassen} aria-busy={laedt || undefined} {...rest} disabled={rest.disabled || laedt}>
      {laedt ? <LoaderCircle className="im-dreht" strokeWidth={1.5} aria-hidden="true" />
        : Icon ? <Icon strokeWidth={1.5} aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

export type StatusArt = "gut" | "warnung" | "kritisch" | "info" | "signal" | "neutral";

const STATUS_KLASSE: Record<StatusArt, string> = {
  gut: "im-badge--good", warnung: "im-badge--warn", kritisch: "im-badge--critical", info: "im-badge--info",
  signal: "im-badge--signal", neutral: "im-badge--plain",
};

/** Status immer mit Wort – nie Farbe allein. */
export function Status({ art = "neutral", children }: { art?: StatusArt; children: ReactNode }) {
  return <span className={`im-badge ${STATUS_KLASSE[art]}`}>{children}</span>;
}

/** Kennzahl mit Quellzeile. `zu`: die Kachel ist ein Link in das Programm, dem die Zahl gehört; `signal`: Frist-Kachel
 *  in Signalfarbe (Entwurf Startseite 01.11.2026). */
export function Kennzahl({ titel, wert, delta, deltaRichtung, quelle, faellig, onClick, zu, signal, className }: {
  titel: ReactNode; wert: ReactNode; delta?: ReactNode; deltaRichtung?: "hoch" | "runter" | "neutral"; quelle: ReactNode;
  faellig?: boolean; onClick?: () => void; zu?: string; signal?: boolean; className?: string;
}) {
  const deltaKlasse = deltaRichtung === "hoch" ? "im-kpi__delta im-kpi__delta--up"
    : deltaRichtung === "runter" ? "im-kpi__delta im-kpi__delta--down" : "im-kpi__delta im-muted";
  const klassen = ["im-kpi", faellig && "im-kpi--due", signal && "im-kpi--signal", className].filter(Boolean).join(" ");
  const inhalt = (
    <>
      <span className="im-label">{titel}</span>
      <span className="im-kpi__value">{wert}</span>
      {delta !== undefined && <span className={deltaKlasse}>{delta}</span>}
      <span className="im-kpi__source">{quelle}</span>
    </>
  );
  if (zu) return <Link className={klassen} to={zu}>{inhalt}</Link>;
  return (
    <button type="button" className={klassen} onClick={onClick} disabled={!onClick}
      style={onClick ? undefined : { cursor: "default" }}>
      {inhalt}
    </button>
  );
}

export function Kennzahlen({ children }: { children: ReactNode }) {
  return <div className="im-kpis">{children}</div>;
}

/** Leer-, Fehler- und Hinweiszustand – überall gleich. */
export function Zustand({ symbol: Icon, titel, children, aktion }: {
  symbol?: LucideIcon; titel: string; children?: ReactNode; aktion?: ReactNode;
}) {
  return (
    <div className="im-state">
      {Icon && <Icon size={24} strokeWidth={1.5} aria-hidden="true" />}
      <h3 className="im-state__title">{titel}</h3>
      {children && <span>{children}</span>}
      {aktion}
    </div>
  );
}

export function Laden({ zeilen = 3 }: { zeilen?: number }) {
  return (
    <div className="im-laden" aria-busy="true" aria-label="Lädt">
      {Array.from({ length: zeilen }, (_, i) => (
        <div key={i} className="im-skeleton" style={{ width: `${[70, 100, 55, 85, 60][i % 5]}%` }} />
      ))}
    </div>
  );
}

export function Banner({ art, marke, children }: { art: "spiegel" | "fehler"; marke: string; children: ReactNode }) {
  return (
    <div className={`im-banner im-banner--${art}`} role={art === "fehler" ? "alert" : "status"}>
      <Status art={art === "fehler" ? "kritisch" : "info"}>{marke}</Status>
      <span>{children}</span>
    </div>
  );
}

export function Feld({ label, hilfe, fehler, ...rest }: InputHTMLAttributes<HTMLInputElement> & {
  label: string; hilfe?: ReactNode; fehler?: string | null;
}) {
  const id = useId();
  return (
    <label className="im-field" htmlFor={id}>
      <span>{label}</span>
      <input id={id} className="im-input" aria-invalid={fehler ? true : undefined}
        aria-describedby={fehler || hilfe ? `${id}-hilfe` : undefined} {...rest} />
      {(fehler || hilfe) && (
        <span id={`${id}-hilfe`} className={fehler ? "im-help im-help--error" : "im-help"}>{fehler || hilfe}</span>
      )}
    </label>
  );
}

export function Auswahl({ label, optionen, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string; optionen: readonly { wert: string; text: string }[];
}) {
  const id = useId();
  return (
    <label className="im-field" htmlFor={id}>
      <span>{label}</span>
      <select id={id} className="im-select" {...rest}>
        {optionen.map((o) => <option key={o.wert} value={o.wert}>{o.text}</option>)}
      </select>
    </label>
  );
}

export function Segment<T extends string>({ wert, optionen, onChange, label }: {
  wert: T; optionen: readonly { wert: T; text: string }[]; onChange: (w: T) => void; label: string;
}) {
  return (
    <div className="im-segment" role="group" aria-label={label}>
      {optionen.map((o) => (
        <button key={o.wert} type="button" aria-pressed={o.wert === wert} onClick={() => onChange(o.wert)}>{o.text}</button>
      ))}
    </div>
  );
}

export function Chip({ aktiv, anzahl, onClick, children }: {
  aktiv?: boolean; anzahl?: number; onClick?: () => void; children: ReactNode;
}) {
  return (
    <button type="button" className="im-chip" aria-pressed={aktiv ?? false} onClick={onClick}>
      {children}
      {anzahl !== undefined && <span className="im-chip__count">{anzahl.toLocaleString("de-DE")}</span>}
    </button>
  );
}

export function Seitenkopf({ titel, children }: { titel: ReactNode; children?: ReactNode }) {
  return (
    <div className="im-seitenkopf">
      <h1 className="im-t-title">{titel}</h1>
      <div className="im-seitenkopf__aktionen">{children}</div>
    </div>
  );
}

export function Quelle({ children }: { children: ReactNode }) {
  return <div className="im-small">Quelle: {children}</div>;
}
